import React, { useState, useEffect } from "react";
import { Input, Button, Form, Alert, Card, Spin } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import flag from "../SignIn/TanzaniaFlag3.avif";
import emblem from "../SignIn/TanzaniaEmblem1.jpeg";
import "../SignIn/styles.css";
import API from "../../../../API";
import { notifySuccess, notifyError } from "../../../../Util";
import PasswordValidationIndicator from "../../../../Util/PasswordValidationIndicator";

/**
 * @component
 * @name AcceptInvitation
 * @description Accept invitation form component
 */
const AcceptInvitation = () => {
  const [loading, setLoading] = useState(false);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [error, setError] = useState("");
  const [invitation, setInvitation] = useState(null);
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, requirements: [] });
  const [form] = Form.useForm();
  const history = useHistory();

  const getUrlParams = () => {
    const hash = window.location.hash;
    if (hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const urlParams = new URLSearchParams(queryString);
      return {
        token: urlParams.get('token')
      };
    }
    const urlParams = new URLSearchParams(window.location.search);
    return {
      token: urlParams.get('token')
    };
  };

  const { token } = getUrlParams();

  useEffect(() => {
    if (!token) {
      setError("Invalid invitation link. Token is missing.");
      setLoadingInvitation(false);
      return;
    }

    // Load invitation details
    const loadInvitation = async () => {
      try {
        const response = await API.getInvitationDetails(token);
        if (response.success) {
          setInvitation(response.data);
        } else {
          setError(response.message || "Invalid or expired invitation token");
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || "Invalid or expired invitation token";
        setError(errorMessage);
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitation();
  }, [token]);

  const handlePasswordChange = (password) => {
    if (password) {
      const validation = API.validatePassword(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation({ isValid: false, requirements: [] });
    }
  };

  const onFinish = async (values) => {
    if (!passwordValidation.isValid) {
      notifyError("Please ensure your password meets all security requirements");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await API.acceptInvitation({
        token,
        password: values.password,
        password_confirmation: values.password_confirmation
      });

      if (response.success) {
        notifySuccess("Account created successfully! You can now log in.");
        history.push('/signin');
      } else {
        const errorMessage = response.message || "Failed to accept invitation";
        setError(errorMessage);
        notifyError(errorMessage);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to accept invitation. Please try again.";
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingInvitation) {
    return (
      <div className="SignIn">
        <div className="SignInForm">
          <img src={flag} alt="Tanzanian flag" className="national-flag"></img>
          <div className="content-body">
            <Card style={{ width: '400px', maxWidth: '90%' }}>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
                <p style={{ marginTop: '20px' }}>Loading invitation details...</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="SignIn">
        <div className="SignInForm">
          <img src={flag} alt="Tanzanian flag" className="national-flag"></img>
          <div className="content-body">
            <img
              src={emblem}
              alt="Tanzanian national emblem"
              className="national-embelem"
            ></img>
            <Card style={{ width: '400px', maxWidth: '90%' }}>
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Alert
                  message="Invalid Invitation"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
                <Link to="/signin">
                  <Button type="primary">Go to Sign In</Button>
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="SignIn">
      <style>{`
        @media (max-width: 768px) {
          .SignIn .ant-form-item {
            margin-bottom: 7px !important;
          }
          .SignIn .ant-form-item:last-child {
            margin-bottom: 0;
          }
        }
      `}</style>
      <div className="SignInForm">
        <img src={flag} alt="Tanzanian flag" className="national-flag"></img>
        <div className="content-body">
          <img
            src={emblem}
            alt="Tanzanian national emblem"
            className="national-embelem"
          ></img>
          <Card style={{ width: '400px', maxWidth: '90%' }}>
            <div className="form-container">
               <h1 style={{ textAlign: 'center', marginBottom: '10px', fontSize: '24px', fontWeight: 'bold' }}>Project Supervision Tool</h1>
              <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>Accept Invitation</h2>
              
              {invitation && (
                <div style={{ marginBottom: 20, padding: '8px 15px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                  <p style={{ padding: 0, margin: 0, textAlign: 'center' }}><strong>Email:</strong> {invitation.email}</p>
                </div>
              )}

              {error && (
                <Alert
                  message="Error"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 20 }}
                />
              )}

              <Form
                form={form}
                name="accept-invitation"
                onFinish={onFinish}
                layout="vertical"
                size="large"
                data-testid="accept-invitation-form"
              >
                <Form.Item
                  name="password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                    () => ({
                      validator(_, value) {
                        if (!value || passwordValidation.isValid) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Password does not meet security requirements'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter your password"
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    data-testid="accept-invitation-password-input"
                  />
                </Form.Item>

                {passwordValidation.requirements.length > 0 && (
                  <PasswordValidationIndicator validation={passwordValidation} />
                )}

                <Form.Item
                  name="password_confirmation"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm your password"
                    data-testid="accept-invitation-password-confirmation-input"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    disabled={!passwordValidation.isValid}
                    data-testid="accept-invitation-submit-button"
                  >
                    Complete Account Setup
                  </Button>
                </Form.Item>
              </Form>

              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to="/signin">Already have an account? Sign In</Link>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitation;
